import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, ViewChild, AfterViewInit, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { TrainingGoal } from '@cf-shared/enums';
import { CsCardInterfaceClass, CsCardComponent } from 'components/cs-card/cs-card';
import { LL } from 'i18n/i18n.service';
import { MacroData, MacroSet, NutritionPlanData } from './nutrition-plan-compute';

export enum MacroTabView {
  // cycling enabled tabs
  Day = 'day',
  BulkMeal = 'bulk',
  CutMeal = 'cut',
  // cycling disabled tabs
  PerDay = 'perDay',
  PerMeal = 'perMeal'
}

@Component({
  selector: 'cs-nutrition-plan-card',
  templateUrl: 'cs-nutrition-plan-card.html',
  host: { 'class': 'nutrition-plan-card' },
  providers: [{ provide: CsCardInterfaceClass, useExisting: CsNutritionPlanCard }],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsNutritionPlanCard extends CsCardInterfaceClass implements AfterViewInit, OnChanges {
  protected LL = LL;
  @ViewChild(CsCardComponent) card: CsCardComponent;

  @Input() nutrition: NutritionPlanData;
  @Input() trainingGoal: TrainingGoal;
  @Input() editable: boolean = true;

  @Output() edit = new EventEmitter<void>();

  TrainingGoal = TrainingGoal;
  MacroTabView = MacroTabView;

  selectedMacroTab: MacroTabView = MacroTabView.Day;

  constructor(elemRef: ElementRef) {
    super(elemRef);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // set default tab based on cycling mode
    if (changes['nutrition'] && this.nutrition) {
      this.selectedMacroTab = this.nutrition.isCyclingEnabled
        ? MacroTabView.Day
        : MacroTabView.PerDay;
    }
  }

  ngAfterViewInit(): void {
    // Ensure card is initialized
    if (this.card) {
      this.card.updateLayout();
    }
  }

  updateLayout(): void {
    if (this.card) {
      this.card.updateLayout();
    }
  }

  getCurrentCardHeight(): number {
    return this.card ? this.card.getCurrentCardHeight() : 0;
  }

  getCardElement(): HTMLElement {
    return this.elemRef.nativeElement;
  }

  onEditClick() {
    this.edit.emit();
  }

  getMacroValue(macro: string | MacroData | undefined): string {
    if (!macro) return '';
    return typeof macro === 'string' ? macro : macro.value;
  }

  getMacroSuffix(macro: string | MacroData | undefined): string | undefined {
    if (!macro || typeof macro === 'string') return undefined;
    if (macro.suffix === 'min') return LL.intake.planOverview.macroMin();
    if (macro.suffix === 'max') return LL.intake.planOverview.macroMax();
    return undefined;
  }

  getCurrentMacros(): MacroSet | null {
    if (!this.nutrition) return null;

    switch (this.selectedMacroTab) {
      // cycling enabled tabs
      case MacroTabView.BulkMeal:
        return this.nutrition.bulkMealMacros || null;
      case MacroTabView.CutMeal:
        return this.nutrition.cutMealMacros || null;
      // cycling disabled tabs
      case MacroTabView.PerMeal:
        return this.nutrition.fixedMealMacros || null;
      case MacroTabView.Day:
      case MacroTabView.PerDay:
      default:
        return {
          protein: this.nutrition.protein,
          carbs: this.nutrition.carbs,
          fat: this.nutrition.fat,
          fiber: this.nutrition.fiber
        };
    }
  }

  getMacroTitle(): string {
    switch (this.selectedMacroTab) {
      case MacroTabView.Day:
        return LL.intake.planOverview.macroTitles.estimatedDailyAverage();
      case MacroTabView.BulkMeal:
        return LL.intake.planOverview.macroTitles.bulkMeal();
      case MacroTabView.CutMeal:
        return LL.intake.planOverview.macroTitles.cutMeal();
      case MacroTabView.PerDay:
        return LL.intake.planOverview.macroTitles.daily();
      case MacroTabView.PerMeal:
        return LL.intake.planOverview.macroTitles.meal();
    }
  }

  get trainingGoalText(): string {
    if (this.trainingGoal === TrainingGoal.MuscleGrowth) {
      return LL.intake.goals.muscleGrowth();
    }
    return LL.intake.goals.fatLoss();
  }
}
